import client from './client';
import type { PowerData } from './dashboard';

export type ProductionBucket = 'year' | 'month' | 'day' | 'hour';
export interface ProductionPeriod { bucket: ProductionBucket; keys: number[]; label: string; unit: 'kWh' | 'MWh' }
export interface InverterProductionRow { inverter_id: number; inverter_name: string; production_kwh: number }

export async function fetchInverterProduction(uuid: string, period: ProductionPeriod, signal?: AbortSignal) {
  return (await client.get<{ rows: InverterProductionRow[] }>(`/powerplants/${uuid}/inverter-production`, {
    params: { bucket: period.bucket, period_keys: period.keys.join(',') }, signal,
  })).data;
}

export function buildProductionPeriod(bucket: ProductionBucket, rows: PowerData[]): ProductionPeriod {
  const keys = [...new Set(rows.map(row => {
    const year = Number(row.date_year), month = Number(row.date_month), day = Number(row.date_day), hour = Number(row.date_hour);
    if (!Number.isInteger(year) || year < 2000 || year > 2100) return null;
    if (bucket === 'year') return year;
    if (!Number.isInteger(month) || month < 1 || month > 12) return null;
    if (bucket === 'month') return year * 100 + month;
    if (!Number.isInteger(day) || day < 1 || day > new Date(Date.UTC(year, month, 0)).getUTCDate()) return null;
    if (bucket === 'day') return year * 10000 + month * 100 + day;
    return row.date_hour != null && Number.isInteger(hour) && hour >= 0 && hour <= 23 ? year * 1000000 + month * 10000 + day * 100 + hour : null;
  }).filter((key): key is number => key !== null))].sort((a, b) => a - b);
  function label(key: number) {
    if (bucket === 'year') return String(key);
    const dateKey = bucket === 'hour' ? Math.floor(key / 100) : key;
    const year = bucket === 'month' ? Math.floor(key / 100) : Math.floor(dateKey / 10000);
    const month = bucket === 'month' ? key % 100 : Math.floor(dateKey / 100) % 100;
    return new Intl.DateTimeFormat('tr-TR', { year: 'numeric', month: 'long', ...(bucket !== 'month' ? { day: 'numeric' } : {}), timeZone: 'UTC' })
      .format(new Date(Date.UTC(year, month - 1, bucket === 'month' ? 1 : dateKey % 100)));
  }
  const first = keys.length ? label(keys[0]) : 'Veri yok', last = keys.length ? label(keys[keys.length - 1]) : first;
  return { bucket, keys, label: first === last ? first : `${first} – ${last}`, unit: bucket === 'hour' ? 'kWh' : 'MWh' };
}
